import { useRef } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { KnowledgeBaseItem } from '@/services/knowledgeBase.service';
import { formatDateTime } from '@/utils/formatDate';

const proposalWrittenAt = (proposal: KnowledgeBaseItem) => proposal.last_updated || proposal.created_at;

interface StaleUpdateDialogProps {
  article?: KnowledgeBaseItem;
  proposal: KnowledgeBaseItem | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Shown when the server refuses an approval because the article changed after the update was proposed (ENG-232). */
export default function StaleUpdateDialog({ article, proposal, onConfirm, onCancel }: StaleUpdateDialogProps) {
  // Keep the last proposal so the text doesn't blank out during the close animation.
  const shown = useRef(proposal);
  if (proposal) shown.current = proposal;
  const p = shown.current;

  return (
    <AlertDialog open={proposal !== null} onOpenChange={open => !open && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>This update is out of date</AlertDialogTitle>
          <AlertDialogDescription>
            {p && article
              ? `It was proposed on ${formatDateTime(proposalWrittenAt(p))}, but the article changed after that, on ${formatDateTime(article.last_updated)}. Approving replaces the current text with this older version and undoes that change.`
              : null}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Approve anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
